import Box from '@mui/material/Box';
import Shimmer from './Shimmer';
import { plans, plan, planPop, planChk } from '../../styles/tl5';

// Skeleton for the Membership pricing grid.
//
// It reuses `plans`, `plan`, `planPop` and `planChk` rather than restating
// their geometry. The previous version hardcoded its own card — translucent
// white on an assumed forest-green page — and had drifted badly: the real
// cards are white with the featured one inverted to near-black, so the
// skeleton flashed a dark grid before a light one arrived.
//
// Mirrors one card: title row (+ "Most popular" pill on the featured one)
// · big price · tagline · divider · perk rows (circle + text) · CTA.

// On the inverted card the `sk` sweep reads as near-white, so blocks there
// are re-tinted. The override has to replace `background`: `sk` paints a
// gradient, and a `bgcolor` would sit underneath it and never show.
const sweep = (rgb, lo, hi) => ({
  background: `linear-gradient(90deg, rgba(${rgb},${lo}) 0%, rgba(${rgb},${hi}) 45%, rgba(${rgb},${lo}) 90%)`,
});
const tint = (featured, lo, hi) => (featured ? sweep('255,255,255', lo, hi) : null);
// The featured card's pill and CTA are yellow in the real layout.
const YELLOW = sweep('252,235,0', 0.3, 0.5);

const PlanCardShimmer = ({ featured = false, perks = 5 }) => (
  <Box sx={{ ...(featured ? planPop : plan), transform: 'none', '&:hover': undefined }}>
    {/* Title + the featured card's pill */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
      <Shimmer width={132} height={22} rounded={6} sx={tint(featured, 0.1, 0.2)} />
      {featured && <Shimmer width={104} height={24} rounded={999} sx={YELLOW} />}
    </Box>

    {/* Price, then the "/ year" beside it */}
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', mt: '14px' }}>
      <Shimmer width={148} height={44} rounded={8} sx={tint(featured, 0.12, 0.22)} />
      <Shimmer width={46} height={15} rounded={4} sx={tint(featured, 0.08, 0.16)} />
    </Box>

    {/* Tagline */}
    <Shimmer width="72%" height={14} rounded={4} sx={{ mt: '8px', ...tint(featured, 0.07, 0.14) }} />

    <Box sx={{ height: '1px', background: featured ? 'rgba(255,255,255,.14)' : 'var(--tl-line)', my: '22px' }} />

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {Array.from({ length: perks }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <Box sx={{ ...planChk, background: featured ? 'rgba(255,255,255,.12)' : 'var(--tl-teal-50)' }} />
          <Shimmer
            width={`${58 + ((i * 13) % 34)}%`}
            height={15}
            rounded={4}
            sx={tint(featured, 0.08, 0.16)}
          />
        </Box>
      ))}
    </Box>

    {/* CTA — `mt: auto` so it sits on the floor of the tallest card, as the
        real one does. */}
    <Box sx={{ mt: 'auto', pt: '26px' }}>
      <Shimmer width="100%" height={48} rounded={999} sx={featured ? YELLOW : null} />
    </Box>
  </Box>
);

const MembershipShimmer = ({ count = 3 }) => (
  <Box sx={plans}>
    {Array.from({ length: count }).map((_, i) => (
      <PlanCardShimmer key={i} featured={count >= 3 ? i === 1 : i === 0} perks={5 + (i % 2)} />
    ))}
  </Box>
);

export default MembershipShimmer;
